import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  ListItemIcon,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { RejectIcon, ReloadIcon } from "../../../../components/MuiIcons";
import UndoIcon from "@mui/icons-material/Undo";
import { PanelLayout } from "../PanelLayout";
import { FC } from "react";
import { ActionButton } from "./styles";
import SendIcon from "@mui/icons-material/Send";
import { TVerificationStatusActionsResult } from "../../hooks/useVerificationStatusActions";
import { RETURN_TO_REVISION, VerificationStatus } from "../../utils/getStatus";
import { CompanyRole } from "../../../../auth/AuthProvider";
import { ICompanyVerificationStatusesConfig } from "../../network";
import { getDarker } from "../../utils/getColorDarker";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const actionIcons: Partial<Record<VerificationStatus, React.ReactNode>> = {
  [VerificationStatus.INFORMATION_REQUESTED]: <ReloadIcon />,
  [VerificationStatus.SENT_TO_VERIFICATION]: <SendIcon color='primary' />,
};

export const ActionPanel: FC<{
  onAction: (value: string, to?: VerificationStatus) => void;
  status: VerificationStatus;
  loading: boolean;
  companyRole: CompanyRole;
  statusReason?: string;
  riskLevel: { riskLevel: string; riskLevelReason: string } | null;
  actions: TVerificationStatusActionsResult;
  statusesConfig: ICompanyVerificationStatusesConfig;
}> = ({
  onAction,
  actions,
  status,
  statusReason,
  riskLevel,
  companyRole,
  statusesConfig,
  loading,
}) => {
  return (
    <PanelLayout title='Actions'>
      <List>
        {actions.actions.map(
          ({ displayName, description, name, to }, index) => {
            return (
              <ListItem
                disablePadding
                key={displayName}
                sx={{
                  margin: "8px 0",
                  borderBottom: "1px solid #F4F4F4",
                }}
              >
                {index === 0 && loading ? (
                  <CircularProgress style={{ marginBottom: 14 }} size={30} />
                ) : (
                  <ListItemButton
                    sx={{ margin: "0", padding: "0", flexWrap: "wrap" }}
                    onClick={() => onAction(name, to)}
                  >
                    {name === RETURN_TO_REVISION ? (
                      <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
                        <UndoIcon color='primary' />
                      </ListItemIcon>
                    ) : to in actionIcons ? (
                      <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
                        {actionIcons[to]}
                      </ListItemIcon>
                    ) : null}
                    <ListItemText
                      primary={displayName}
                      primaryTypographyProps={{
                        fontWeight: "700",
                        fontSize: "14px",
                        lineHeight: "22px",
                        letterSpacing: "0.5px",
                      }}
                    />
                    <Typography fontSize={"14px"} mb='1em' width='100%'>
                      {description}
                    </Typography>
                  </ListItemButton>
                )}
              </ListItem>
            );
          }
        )}

        {riskLevel && (
          <ListItem
            disablePadding
            key={"Risk Level"}
            sx={{
              margin: "8px 0",
              borderBottom: "1px solid #F4F4F4",
            }}
          >
            <ListItemButton
              sx={{ margin: "0", padding: "0", flexWrap: "wrap" }}
              onClick={() => onAction("changeRiskLevel")}
              disabled={companyRole === CompanyRole.VIEWER}
            >
              <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
                <TrendingUpIcon color='primary' />
              </ListItemIcon>
              <ListItemText
                primary={"Risk Level"}
                primaryTypographyProps={{
                  fontWeight: "700",
                  fontSize: "14px",
                  lineHeight: "22px",
                  letterSpacing: "0.5px",
                }}
              />
              <Typography fontSize={"14px"} mb='1em' width='100%'>
                {`${riskLevel.riskLevel} ${
                  riskLevel.riskLevelReason
                    ? ": " + riskLevel.riskLevelReason
                    : ""
                }`}
              </Typography>
            </ListItemButton>
          </ListItem>
        )}
        {statusReason && (
          <ListItem
            disablePadding
            key={"Action Reason"}
            sx={{
              margin: "8px 0",
              borderBottom: "1px solid #F4F4F4",
            }}
          >
            <ListItemButton
              sx={{ margin: "0", padding: "0", flexWrap: "wrap" }}
              onClick={() => onAction("changeStatusReason")}
              disabled={companyRole === CompanyRole.VIEWER}
            >
              <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
                <RejectIcon fill={statusesConfig.object[status]?.color} />
              </ListItemIcon>
              <ListItemText
                primary={"Last action reason"}
                primaryTypographyProps={{
                  color: statusesConfig.object[status]?.color,
                  fontWeight: "700",
                  fontSize: "14px",
                  lineHeight: "22px",
                  letterSpacing: "0.5px",
                }}
              />
              <Typography fontSize={"14px"} mb='1em' width='100%'>
                {statusReason}
              </Typography>
            </ListItemButton>
          </ListItem>
        )}
      </List>
      <Stack
        flexDirection='row'
        alignItems='center'
        justifyContent='flex-end'
        width='100%'
        height='100%'
        gap='16px'
      >
        {actions.buttons.map(({ displayName, name, to }) => {
          const mainColor = statusesConfig.object[to]?.color;
          return (
            <Box flexGrow={1}>
              <ActionButton
                variant='contained'
                sx={{
                  backgroundColor: mainColor,
                  "&:hover": { backgroundColor: getDarker(mainColor, 0.7) },
                }}
                onClick={() => onAction(name, to)}
              >
                {displayName}
              </ActionButton>
            </Box>
          );
        })}
      </Stack>
    </PanelLayout>
  );
};
